// src/products/analysis/pages/NewsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, ExternalLink, Calendar } from 'lucide-react';
import { getCompanyNews } from '../services/finnhubService';

const NewsPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState(symbol);

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const cleanSymbol = symbol.toUpperCase().trim();
      const newsData = await getCompanyNews(cleanSymbol);
      
      if (!newsData || newsData.length === 0) {
        setError(`No news articles found for ${cleanSymbol}.`);
        setNews([]);
      } else {
        setNews(newsData);
        setCompanyName(cleanSymbol);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to load news. Please try again.';
      setError(errorMessage);
      console.error('News loading error:', err);
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    if (symbol) {
      loadNews();
    }
  }, [symbol, loadNews]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{companyName} News</h1>
            <p className="text-sm text-gray-500">Latest company news and updates</p>
          </div>
        </div>
      </div>

      {/* News Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading news...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && news.length > 0 && (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {news.length} Recent Articles
              </h2>
              <button
                onClick={loadNews}
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Refresh
              </button>
            </div>

            <div className="space-y-4">
              {news.map((article, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 transition-colors"
                        >
                          {article.headline}
                        </a>
                      </h3>
                      
                      {article.summary && article.summary !== 'No summary available' && (
                        <p className="text-gray-600 mb-3 line-clamp-3">
                          {article.summary}
                        </p>
                      )}

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(article.datetime)}
                        </span>
                        <span className="font-medium text-gray-700">
                          {article.source}
                        </span>
                        {article.category && (
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                            {article.category}
                          </span>
                        )}
                      </div>
                    </div>

                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                      title="Read full article"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsPage;



